<script setup lang="ts">
import {computed, onMounted, onUnmounted, ref, watchEffect} from 'vue'
import {ListDetailLayout} from '@hantera/design-system'
import {useContext, useDialogs, useLocalization} from '@hantera/portal-app'
import useBackend from '@/composition/useBackend'
import ClientDetails from './ClientDetails.vue'
import CreateClientModal from './CreateClientModal.vue'
import {ClientsDataProvider} from './data-provider'
import {useIamEventsStore} from '@/stores/iam/iam-events-store'
import type {ClientsListParams} from '@/backend/iam.types'

const context = useContext()
const { iam: iamService } = useBackend()
const t = useLocalization('iam.clients')
const dialogs = useDialogs()
const iamEvents = useIamEventsStore()

watchEffect(() => {
  if (context.currentView.value?.name) {
    context.currentView.value.name.value = t('viewTitle')
  }
})

const canRead = computed(() => context.authorize('iam/clients:read'))
const canWrite = computed(() => context.authorize('iam/clients:write'))

const filters = ref<ClientsListParams>({
  includeSuspended: true,
  limit: 100,
})

const clientsProvider = new ClientsDataProvider(filters, iamService, t)

const selectedClientId = ref<string | null>(null)

function openCreateModal() {
  dialogs.openModal(t('create.title'), CreateClientModal, true, {
    iamService,
    onCreated: (client: any) => {
      // Refresh list and select new client
      clientsProvider.refresh()
      selectedClientId.value = client.id
    },
    onCancel: () => {
      // Modal will close automatically
    },
  })
}

let unsubscribe: (() => void) | null = null

onMounted(() => {
  if (!canRead.value) {
    context.currentView.value?.close()
  }

  unsubscribe = iamEvents.subscribeToClientsList(() => {
    clientsProvider.refresh()
  })
})

onUnmounted(() => {
  unsubscribe?.()
})
</script>

<template>
  <div class="clients-view">
    <ListDetailLayout
      :provider="clientsProvider"
      :detail-component="ClientDetails"
      cache-key="iam-clients"
      :selected="selectedClientId"
      @update:selected="(val: string | null) => selectedClientId = val"
      :allow-multi-select="false"
    >
      <template #list-toolbar>
        <div class="toolbar-content">
          <button v-if="canWrite" @click="openCreateModal" class="btn-primary">
            {{ t('create.button') }}
          </button>
        </div>
      </template>
    </ListDetailLayout>
  </div>
</template>

<style scoped>
.clients-view {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.toolbar-content {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  width: 100%;
}
</style>
